import { useEffect, useRef, useState } from 'react';
import { get, post } from '../../lib/api.js';
import { errorMessage } from '../../lib/errors.js';
import { formatDateTime, formatMoney } from '../../lib/format.js';
import { useT } from '../../lib/i18n.jsx';

// Domain orders that still need attention (unpaid, being processed, failed),
// plus the order the customer just paid for when Mollie sends them back to
// /domeinen?order=<id>. That one is first fetched with ?refresh=true (the API
// then asks Mollie itself, in case the webhook is late) and polled while the
// payment or the registration is in progress.
const OPEN = ['pending', 'paid', 'provisioning', 'failed'];
const BUSY = ['paid', 'provisioning'];
const POLL_MS = 4000;
const POLL_MAX = 10;

export default function DomainOrders({ lang, orders, returnedOrderId, onDismissReturned, onChanged }) {
  const t = useT();
  const [returned, setReturned] = useState(null);
  const [returnedError, setReturnedError] = useState(null);
  const [paying, setPaying] = useState(null);
  const [payError, setPayError] = useState('');
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  useEffect(() => {
    if (!returnedOrderId) {
      setReturned(null);
      return undefined;
    }
    let alive = true;
    let timer = null;
    let polls = 0;
    let lastStatus = null;
    const load = async (refresh) => {
      try {
        const o = await get(`/dns/domains/orders/${returnedOrderId}${refresh ? '?refresh=true' : ''}`);
        if (!alive) return;
        setReturned(o);
        setReturnedError(null);
        if (o.status !== lastStatus) {
          lastStatus = o.status;
          onChangedRef.current?.();
        }
        const keepPolling = BUSY.includes(o.status) || (o.status === 'pending' && polls < 3);
        if (keepPolling && polls < POLL_MAX) {
          polls += 1;
          timer = setTimeout(() => load(o.status === 'pending'), POLL_MS);
        }
      } catch (e) {
        if (alive) setReturnedError(e);
      }
    };
    load(true);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [returnedOrderId]);

  const pay = async (order) => {
    setPaying(order.id);
    setPayError('');
    try {
      const res = await post(`/dns/domains/orders/${order.id}/pay`);
      window.location.assign(res.checkout_url);
    } catch (e) {
      setPaying(null);
      setPayError(e?.payload?.detail?.code === 'not_payable' ? t('orders.err.notPayable') : errorMessage(e, { lang }));
      onChangedRef.current?.();
    }
  };

  const others = (orders || []).filter((o) => OPEN.includes(o.status) && String(o.id) !== String(returnedOrderId));
  if (!returnedOrderId && others.length === 0) return null;

  return (
    <section className="domain-orders" aria-label={t('orders.title')}>
      {returnedOrderId && (
        <div className={'order-card order-card--' + (returned ? toneOf(returned.status) : 'neutral')} role="status">
          {!returned && !returnedError && <p className="order-card__msg">{t('orders.checking')}</p>}
          {returnedError && <p className="order-card__msg">{errorMessage(returnedError, { lang })}</p>}
          {returned && <OrderBody order={returned} lang={lang} t={t} highlight />}
          <div className="order-card__actions">
            {returned?.status === 'pending' && (
              <button type="button" className="btn-primary" disabled={paying === returned.id} onClick={() => pay(returned)}>
                {t('orders.pay')}
              </button>
            )}
            <button type="button" className="btn-ghost" onClick={onDismissReturned}>
              {t('domainsPage.dismiss')}
            </button>
          </div>
        </div>
      )}
      {others.length > 0 && (
        <div className="card order-list">
          <h2 className="order-list__title">{t('orders.title')}</h2>
          {others.map((o) => (
            <div key={o.id} className="order-row">
              <OrderBody order={o} lang={lang} t={t} />
              {o.status === 'pending' && (
                <div className="order-card__actions">
                  <button type="button" className="btn-ghost" disabled={paying === o.id} onClick={() => pay(o)}>
                    {t('orders.pay')}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {payError && <div className="login-error" role="alert">{payError}</div>}
    </section>
  );
}

function toneOf(status) {
  if (status === 'completed') return 'ok';
  if (status === 'failed' || status === 'cancelled') return 'danger';
  return 'warn';
}

const STATUSES = ['pending', 'paid', 'provisioning', 'completed', 'failed', 'cancelled'];

function OrderBody({ order, lang, t, highlight }) {
  const item = order.items?.[0] || {};
  const domain = item.domain_name || order.order_number;
  const action = item.action === 'transfer' ? 'transfer' : 'register';
  const status = STATUSES.includes(order.status) ? order.status : null;
  const tone = toneOf(order.status);
  const message = messageFor(order.status, action, domain, t);
  return (
    <div className="order-body">
      <div className="order-body__head">
        <span className={highlight ? 'order-body__domain order-body__domain--lg' : 'order-body__domain'}>{domain}</span>
        <span className={'status-pill' + (tone === 'danger' ? ' is-danger' : tone === 'warn' ? ' is-warn' : '')}>
          <span className="dot" />
          {status ? t(`orders.status.${status}`) : order.status}
        </span>
      </div>
      <div className="order-body__meta">
        {t(`orders.action.${action}`)} · {formatMoney(order.total, lang)} · {formatDateTime(order.created_at, lang)}
      </div>
      {message && <p className="order-card__msg">{message}</p>}
      {item.status === 'failed' && item.error_code && (
        <p className="order-card__msg">{t(`orders.error.${item.error_code}`)}</p>
      )}
    </div>
  );
}

function messageFor(status, action, domain, t) {
  switch (status) {
    case 'pending':
      return t('orders.msg.pending');
    case 'paid':
    case 'provisioning':
      return t(`orders.msg.provisioning.${action}`);
    case 'completed':
      return t(`orders.msg.completed.${action}`, { domain });
    case 'failed':
      return t('orders.msg.failed', { domain });
    default:
      return '';
  }
}
